import React, { useState } from 'react';
import { authService, userProfileService } from './firebase-services.js';

const AuthModal = ({ onClose, onAuthSuccess, current_user }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('signin'); // 'signin' or 'signup'
  
  // Sign in fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Sign up fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [agreeToPrivacy, setAgreeToPrivacy] = useState(false);
  const [parentConsent, setParentConsent] = useState(false);

  const handleAuth = async (authMethodName) => {
    setLoading(true);
    setError('');
    
    try {
      let result;
      if (authMethodName === 'google') {
        result = await authService.signInWithGoogle();
      } else if (authMethodName === 'email') {
        result = await authService.signInWithEmail(email, password);
      } else if (authMethodName === 'anonymous') {
        result = await authService.signInAnonymously();
      }
      
      if (result.user) {
        onAuthSuccess(result.user);
        onClose();
      } else {
        setError(result.error || 'Authentication failed');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async () => {
    setLoading(true);
    setError('');
    
    // Validation
    if (!firstName || !lastName || !email || !password || !confirmPassword) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }
    
    if (!agreeToTerms || !agreeToPrivacy) {
      setError('Please agree to the Terms of Service and Privacy Policy');
      setLoading(false);
      return;
    }
    
    // Check if user might be under 13 (basic heuristic)
    const ageHeuristic = gradeLevel && ['Pre-K', 'Kindergarten', '1st Grade', '2nd Grade'].includes(gradeLevel);
    if (ageHeuristic && !parentConsent && !parentEmail) {
      setError('Parental consent and parent email are required for students in 2nd grade and below');
      setLoading(false);
      return;
    }
    
    try {
      // Create user account
      const result = await authService.createEmailUser(email, password);
      
      if (result.user) {
        // Create user profile in Firestore
        const profileData = {
          firstName,
          lastName,
          email,
          gradeLevel: gradeLevel || null,
          parentEmail: ageHeuristic ? parentEmail : null,
          parentConsent: ageHeuristic ? parentConsent : null,
          agreeToTerms,
          agreeToPrivacy,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        // Save profile to Firestore
        await userProfileService.saveUserProfile(result.user.uid, profileData);
        
        // Show verification message instead of signing in
        setError('Account created! Please check your email and click the verification link to complete registration.');
        setLoading(false);
        
        // Switch to sign-in mode after successful registration
        setTimeout(() => {
          setMode('signin');
          setError('');
        }, 3000);
      } else {
        setError(result.error || 'Account creation failed');
      }
    } catch (err) {
      console.error('Sign-up error:', err);
      setError(err.message || 'An unexpected error occurred during sign-up');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-3xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Mode Toggle */}
        <div className="flex justify-center mb-6">
          <div className="bg-gray-100 rounded-full p-1">
            <div className="flex">
              <button
                onClick={() => setMode('signin')}
                className={`px-4 py-2 rounded-l-full text-sm font-kindergarten transition-colors ${
                  mode === 'signin' 
                    ? 'bg-white text-primary-600' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => setMode('signup')}
                className={`px-4 py-2 rounded-r-full text-sm font-kindergarten transition-colors ${
                  mode === 'signup' 
                    ? 'bg-white text-primary-600' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Sign Up
              </button>
            </div>
          </div>
        </div>

        <div className="text-center mb-6">
          <h2 className="text-3xl font-kindergarten text-primary-700 mb-4">
            {mode === 'signin' ? 'Sign In' : 'Create Account'}
          </h2>
          <p className="text-gray-600">
            {mode === 'signin' 
              ? 'Choose how you\'d like to sign in to save your flashcards'
              : 'Join us to save your flashcards and track your progress!'
            }
          </p>
        </div>

        {error && (
          <div className={`mb-4 p-3 rounded-2xl text-sm ${
            error.includes('Account created!') 
              ? 'bg-green-100 border border-green-400 text-green-700' 
              : 'bg-red-100 border border-red-400 text-red-700'
          }`}>
            {error}
          </div>
        )}

        {mode === 'signin' ? (
          // SIGN IN FORM
          <div className="space-y-3">
            {/* Google Sign In */}
            <button
              onClick={() => handleAuth('google')}
              disabled={loading}
              className="w-full flex items-center justify-center space-x-3 p-4 border-4 border-gray-300 rounded-2xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="font-kindergarten text-lg">Continue with Google</span>
            </button>

            {/* Email/Password Sign In */}
            <div className="bg-white rounded-2xl p-4 border-4 border-gray-300">
              <div className="space-y-3">
                <div>
                  <input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:border-primary-500 focus:outline-none font-kindergarten"
                  />
                </div>
                <div>
                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:border-primary-500 focus:outline-none font-kindergarten"
                  />
                </div>
                <button
                  onClick={() => handleAuth('email')}
                  disabled={loading || !email || !password}
                  className="w-full btn-primary text-lg py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Sign In with Email
                </button>
              </div>
            </div>

            {/* Anonymous Sign In */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500 font-kindergarten">or</span>
              </div>
            </div>

            <button
              onClick={() => handleAuth('anonymous')}
              disabled={loading}
              className="w-full p-4 border-4 border-primary-300 bg-primary-50 text-primary-700 rounded-2xl hover:bg-primary-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="font-kindergarten text-lg">Continue as Guest</span>
              <p className="text-xs mt-1">Your progress won't be saved</p>
            </button>
          </div>
        ) : (
          // SIGN UP FORM
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-kindergarten text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:border-primary-500 focus:outline-none font-kindergarten"
                />
              </div>
              <div>
                <label className="block text-sm font-kindergarten text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:border-primary-500 focus:outline-none font-kindergarten"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-kindergarten text-gray-700 mb-1">Email Address</label>
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:border-primary-500 focus:outline-none font-kindergarten"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-kindergarten text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  placeholder="Create password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:border-primary-500 focus:outline-none font-kindergarten"
                />
              </div>
              <div>
                <label className="block text-sm font-kindergarten text-gray-700 mb-1">Confirm Password</label>
                <input
                  type="password"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:border-primary-500 focus:outline-none font-kindergarten"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-kindergarten text-gray-700 mb-1">Grade Level (Optional)</label>
              <select
                value={gradeLevel}
                onChange={(e) => setGradeLevel(e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:border-primary-500 focus:outline-none font-kindergarten"
              >
                <option value="">Select grade level</option>
                <option value="Pre-K">Pre-K</option>
                <option value="Kindergarten">Kindergarten</option>
                <option value="1st Grade">1st Grade</option>
                <option value="2nd Grade">2nd Grade</option>
                <option value="3rd Grade">3rd Grade</option>
                <option value="4th Grade">4th Grade</option>
                <option value="5th Grade">5th Grade</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Parental consent section for younger students */}
            {gradeLevel && ['Pre-K', 'Kindergarten', '1st Grade', '2nd Grade'].includes(gradeLevel) && (
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
                <h3 className="text-sm font-kindergarten text-yellow-800 mb-3">👨 Parent Information Required</h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-kindergarten text-gray-700 mb-1">Parent Email</label>
                    <input
                      type="email"
                      placeholder="parent@email.com"
                      value={parentEmail}
                      onChange={(e) => setParentEmail(e.target.value)}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-xl focus:border-primary-500 focus:outline-none font-kindergarten text-sm"
                    />
                  </div>
                  
                  <div className="flex items-start space-x-2">
                    <input
                      type="checkbox"
                      id="parentConsent"
                      checked={parentConsent}
                      onChange={(e) => setParentConsent(e.target.checked)}
                      className="mt-1"
                    />
                    <label htmlFor="parentConsent" className="text-sm text-gray-700 font-kindergarten">
                      I give parental consent for my child to use this app
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Terms and Privacy */}
            <div className="space-y-3">
              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                  className="mt-1"
                />
                <label htmlFor="agreeToTerms" className="text-sm text-gray-700 font-kindergarten">
                  I agree to the <span className="text-primary-600 underline hover:text-primary-800 cursor-pointer">Terms of Service</span>
                </label>
              </div>
              
              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="agreeToPrivacy"
                  checked={agreeToPrivacy}
                  onChange={(e) => setAgreeToPrivacy(e.target.checked)}
                  className="mt-1"
                />
                <label htmlFor="agreeToPrivacy" className="text-sm text-gray-700 font-kindergarten">
                  I agree to the <span className="text-primary-600 underline hover:text-primary-800 cursor-pointer">Privacy Policy</span>
                </label>
              </div>
            </div>

            <button
              onClick={handleSignUp}
              disabled={loading}
              className="w-full btn-primary text-lg py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </div>
        )}

        <div className="mt-6 text-center text-xs text-gray-500">
          {mode === 'signin' 
            ? 'By signing in, you agree to save your flashcards to your account'
            : 'By creating an account, you agree to save your flashcards and track your learning progress'
          }
        </div>

        <button
          onClick={onClose}
          className="mt-4 w-full p-3 text-gray-600 hover:text-gray-800 font-kindergarten"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AuthModal;
